import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight, Edit, Trash2, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths, subMonths, addDays, parseISO, isSameDay } from "date-fns";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  event_time: string | null;
  end_date: string | null;
  end_time: string | null;
  all_day: boolean;
  color?: string | null;
}

interface CustomCalendarProps {
  calendarId?: string;
  calendarIds?: string[]; // For merging multiple calendars
  isAdmin?: boolean;
  onEventEdit?: (event: CalendarEvent) => void;
  onEventDelete?: (eventId: string) => void;
}

// Full 24-color Google Calendar palette
const CALENDAR_COLORS = [
  { name: 'Tomato', value: '#d50000' },
  { name: 'Flamingo', value: '#e67c73' },
  { name: 'Tangerine', value: '#f4511e' },
  { name: 'Banana', value: '#f6bf26' },
  { name: 'Sage', value: '#33b679' },
  { name: 'Basil', value: '#0b8043' },
  { name: 'Peacock', value: '#039be5' },
  { name: 'Blueberry', value: '#3f51b5' },
  { name: 'Lavender', value: '#7986cb' },
  { name: 'Grape', value: '#8e24aa' },
  { name: 'Graphite', value: '#616161' },
  { name: 'Cocoa', value: '#795548' },
  { name: 'Cherry Blossom', value: '#f48fb1' },
  { name: 'Radicchio', value: '#ad1457' },
  { name: 'Mango', value: '#ef6c00' },
  { name: 'Citron', value: '#e4c441' },
  { name: 'Pistachio', value: '#7cb342' },
  { name: 'Eucalyptus', value: '#009688' },
  { name: 'Cobalt', value: '#4285f4' },
  { name: 'Amethyst', value: '#9e69af' },
  { name: 'Birch', value: '#a79b8e' },
  { name: 'Wisteria', value: '#b39ddb' },
  { name: 'Avocado', value: '#c0ca33' },
  { name: 'Pumpkin', value: '#ff6e40' },
];

const CustomCalendar = ({ calendarId, calendarIds, isAdmin = false, onEventEdit, onEventDelete }: CustomCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarName, setCalendarName] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const isMobile = useIsMobile();

  // Determine which calendar IDs to use
  const activeCalendarIds = calendarIds || (calendarId ? [calendarId] : []);

  // Define 2-year navigation limits once for this mounted calendar.
  const { twoYearsAgo, twoYearsFromNow } = useMemo(() => {
    const today = new Date();
    return {
      twoYearsAgo: new Date(today.getFullYear() - 2, today.getMonth(), 1),
      twoYearsFromNow: new Date(today.getFullYear() + 2, today.getMonth() + 1, 0),
    };
  }, []);

  // Helper function to convert 24-hour time to 12-hour format with AM/PM
  const formatTime12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const fetchCalendarInfo = useCallback(async () => {
    if (calendarIds && calendarIds.length > 0) {
      // For merged calendars, check if it's the breakfast/lunch calendar
      const { data, error } = await supabase
        .from('calendars')
        .select('name')
        .in('id', calendarIds);

      if (!error && data && data.length > 0) {
        // Check if this is the breakfast/lunch calendar combination
        const namesLower = data.map(c => c.name.toLowerCase()).join(' ');
        const isBreakfastLunchCalendar = namesLower.includes('breakfast') && namesLower.includes('lunch');
        
        if (isBreakfastLunchCalendar) {
          setCalendarName('CHA Menu - Breakfast, Lunch & Snack - Monthly');
        } else {
          const names = data.map(c => c.name).join(' & ');
          setCalendarName(names);
        }
      }
    } else if (calendarId) {
      const { data, error } = await supabase
        .from('calendars')
        .select('name')
        .eq('id', calendarId)
        .single();

      if (!error && data) {
        // Check if this is a breakfast or lunch calendar
        const nameLower = data.name.toLowerCase();
        const isBreakfastOrLunch = nameLower.includes('breakfast') || nameLower.includes('lunch');
        
        if (isBreakfastOrLunch) {
          setCalendarName('CHA Menu - Breakfast, Lunch & Snack - Monthly');
        } else {
          setCalendarName(data.name);
        }
      }
    }
  }, [calendarId, calendarIds]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);

    // For mobile, fetch all events (2 years range). For desktop, fetch current month range
    let start: Date, end: Date;

    if (isMobile) {
      // Fetch 2 years of events for mobile scrollable view
      start = twoYearsAgo;
      end = twoYearsFromNow;
    } else {
      // Desktop: only fetch current month (no previous month days)
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      start = new Date(monthStart);
      // Skip to first weekday if month starts on weekend
      const dayOfWeek = start.getDay();
      if (dayOfWeek === 0) {
        start.setDate(start.getDate() + 1); // Sunday -> Monday
      } else if (dayOfWeek === 6) {
        start.setDate(start.getDate() + 2); // Saturday -> Monday
      }
      end = new Date(monthEnd);
    }

    // Fetch events that start, end, or span across the visible calendar range
    // Fetch events where event_date <= end AND (end_date IS NULL OR end_date >= start)
    // This captures all events that intersect with our date range
    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');
    
    let query = supabase
      .from('imported_calendar_events')
      .select('*')
      .lte('event_date', endStr)
      .or(`end_date.is.null,end_date.gte.${startStr}`)
      .order('event_date', { ascending: true })
      .order('event_time', { ascending: true, nullsFirst: false });

    // Filter by calendar ID(s)
    if (calendarIds && calendarIds.length > 0) {
      query = query.in('calendar_id', calendarIds);
    } else if (calendarId) {
      query = query.eq('calendar_id', calendarId);
    } else {
      setLoading(false);
      return;
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching events:', error);
    }

    const allEvents: CalendarEvent[] = (data || []).map(event => ({
      id: event.id,
      title: event.title,
      description: event.description,
      event_date: event.event_date,
      event_time: event.event_time,
      end_date: event.end_date,
      end_time: event.end_time,
      all_day: event.all_day,
      color: event.color,
    }));


    setEvents(allEvents);
    setLoading(false);
  }, [calendarId, calendarIds, currentDate, isMobile, twoYearsAgo, twoYearsFromNow]);

  useEffect(() => {
    fetchCalendarInfo();
  }, [fetchCalendarInfo]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleColorChange = async (eventId: string, newColor: string) => {
    const { error } = await supabase
      .from('imported_calendar_events')
      .update({ color: newColor })
      .eq('id', eventId);

    if (error) {
      console.error('Error updating event color:', error);
      toast.error('Failed to update event color');
      return;
    }

    // Update local state
    setEvents(events.map(event =>
      event.id === eventId ? { ...event, color: newColor } : event
    ));

    toast.success('Event color updated');
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const toDateStr = (d: Date) => format(d, 'yyyy-MM-dd');

  const getEventsForDay = (day: Date) => {
    const dayStr = toDateStr(day);
    const filtered = events.filter(event => {
      // Trim whitespace and extract just the date part (in case there's a time component)
      const startStr = event.event_date ? event.event_date.trim().split('T')[0].split(' ')[0] : '';

      // If no end_date, or end_date equals event_date, treat as single-day event
      if (!event.end_date) {
        return startStr === dayStr;
      }

      const endStr = event.end_date ? event.end_date.trim().split('T')[0].split(' ')[0] : '';
      
      // If end_date equals event_date, treat as single-day event
      if (endStr === startStr) {
        return startStr === dayStr;
      }

      // For multi-day events, end_date is exclusive (Google Calendar convention)
      // Compare strings directly - both are in YYYY-MM-DD format
      return startStr <= dayStr && dayStr < endStr;
    });
    return filtered;
  };

  const isEventStart = (day: Date, event: CalendarEvent) => {
    return toDateStr(day) === event.event_date;
  };

  const isEventEnd = (day: Date, event: CalendarEvent) => {
    if (!event.end_date) return true;
    // The last visible day is one day before end_date (exclusive end)
    const dayStr = toDateStr(day);
    const [ey, em, ed] = (event.end_date || event.event_date).split('-').map(Number);
    const endDate = new Date(ey, (em || 1) - 1, (ed || 1));
    endDate.setDate(endDate.getDate() - 1);
    return dayStr === format(endDate, 'yyyy-MM-dd');
  };

  const isEventContinuation = (day: Date, event: CalendarEvent) => {
    const dayStr = toDateStr(day);
    const startStr = event.event_date;
    const endStr = event.end_date ?? startStr;
    // end_date is exclusive, so continuation is between start and end-1
    return startStr < dayStr && dayStr < endStr;
  };

  const previousMonth = () => {
    const prevMonth = subMonths(currentDate, 1);
    if (isAdmin || prevMonth >= twoYearsAgo) {
      setCurrentDate(prevMonth);
    }
  };

  const nextMonth = () => {
    const next = addMonths(currentDate, 1);
    if (isAdmin || next <= twoYearsFromNow) {
      setCurrentDate(next);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  const buildWeeks = () => {
    const weeks: (Date | null)[][] = [];
    const cur = new Date(monthStart);
    
    // Skip to first weekday (Monday-Friday) of the month
    while (cur <= monthEnd) {
      const dayOfWeek = cur.getDay();
      if (dayOfWeek === 0) {
        // Sunday - skip to Monday
        cur.setDate(cur.getDate() + 1);
      } else if (dayOfWeek === 6) {
        // Saturday - skip to Monday
        cur.setDate(cur.getDate() + 2);
      } else {
        break; // Found a weekday (Monday-Friday)
      }
    }
    
    // Build weeks starting from first weekday, continue until we've processed all weekdays in the month
    while (cur <= monthEnd && cur.getMonth() === monthStart.getMonth()) {
      // Skip weekends before starting a new week
      const dayOfWeek = cur.getDay();
      if (dayOfWeek === 0) {
        // Sunday - skip to Monday
        cur.setDate(cur.getDate() + 1);
      } else if (dayOfWeek === 6) {
        // Saturday - skip to Monday
        cur.setDate(cur.getDate() + 2);
      }
      
      // Check if we're still in the month after skipping
      if (cur > monthEnd || cur.getMonth() !== monthStart.getMonth()) {
        break;
      }
      
      const week: (Date | null)[] = [];
      const firstDayOfWeek = cur.getDay(); // 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri
      
      // Pad the beginning of the week if it doesn't start on Monday
      // If first day is Tuesday (2), pad with 1 null
      // If first day is Wednesday (3), pad with 2 nulls
      // If first day is Thursday (4), pad with 3 nulls
      // If first day is Friday (5), pad with 4 nulls
      for (let pad = 1; pad < firstDayOfWeek; pad++) {
        week.push(null);
      }
      
      // Build a week starting from the current day (could be Mon-Fri)
      // Continue until we have 5 weekdays OR we've reached the end of the month
      while (week.length < 5 && cur <= monthEnd && cur.getMonth() === monthStart.getMonth()) {
        const dayOfWeek = cur.getDay();
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          // Monday-Friday - validate date before adding
          if (isNaN(cur.getTime())) {
            console.error('Invalid date created:', cur);
            break;
          }
          week.push(new Date(cur));
          cur.setDate(cur.getDate() + 1);
        } else if (dayOfWeek === 6) {
          // Saturday - we're done with this week (don't show Saturday)
          break;
        } else {
          // Sunday - we're done with this week (don't show Sunday)
          break;
        }
      }
      
      // Pad the end of the week if it doesn't end on Friday
      while (week.length < 5) {
        week.push(null);
      }
      
      // Add week if it has at least one day (keep nulls for proper column alignment)
      if (week.some(d => d !== null)) {
        // Debug: log first week to verify padding
        if (weeks.length === 0 && week.length > 0) {
          console.log('First week structure:', week.map(d => d ? format(d, 'MMM d (E)') : 'null'));
        }
        weeks.push(week);
      }
      
      // If we've gone past the month end or into the next month, we're done
      if (cur > monthEnd || cur.getMonth() !== monthStart.getMonth()) {
        break;
      }
    }
    
    return weeks;
  };

  const weeks = buildWeeks();

  type WeekSegment = {
    event: CalendarEvent;
    startCol: number; // 0-4
    endCol: number;   // 1-5, exclusive index
    roundedL: boolean;
    roundedR: boolean;
    showTitle: boolean;
    lane: number; // which vertical lane this event occupies
  };

  const getWeekSegments = (week: (Date | null)[]): WeekSegment[] => {
    if (week.length === 0) return [];
    
    // Filter out null values for date string generation
    const validDays = week.filter((d): d is Date => d !== null);
    if (validDays.length === 0) return [];
    
    const weekStr = validDays.map(d => {
      if (!d || isNaN(d.getTime())) {
        throw new Error(`Invalid date in week: ${d}`);
      }
      return toDateStr(d);
    });
    const weekStartStr = weekStr[0];
    const lastDay = validDays[validDays.length - 1];
    const weekEndExclusiveStr = toDateStr(addDays(lastDay, 1));

    const intersects = (e: CalendarEvent) => {
      if (!e.end_date) {
        return e.event_date >= weekStartStr && e.event_date < weekEndExclusiveStr;
      }
      return e.event_date < weekEndExclusiveStr && e.end_date > weekStartStr; // using exclusive end
    };

    const segments: WeekSegment[] = [];
    const lanes: { startCol: number; endCol: number }[][] = []; // track occupied columns per lane

    // Only show all-day events in the spanning bars overlay
    // Timed events are shown in day cells, not in the overlay
    const sortedEvents = events.filter(intersects).filter(e => e.all_day).sort((a, b) => {
      // Sort by event_date for all-day events
      return a.event_date.localeCompare(b.event_date);
    });

    sortedEvents.forEach((e) => {
      // Find the column for the event start date (accounting for null padding)
      let startCol = 0;
      for (let col = 0; col < week.length; col++) {
        const day = week[col];
        if (day !== null) {
          const dayStr = toDateStr(day);
          if (dayStr >= e.event_date) {
            startCol = col;
            break;
          }
        }
      }

      // Find the column for the event end date
      let endCol: number;
      if (e.end_date) {
        let idx = startCol;
        for (let col = startCol; col < week.length; col++) {
          const day = week[col];
          if (day !== null) {
            const dayStr = toDateStr(day);
            if (dayStr >= e.end_date) {
              idx = col;
              break;
            }
          }
        }
        endCol = Math.max(startCol + 1, Math.min(week.length, idx + 1));
      } else {
        // single-day event: one column
        endCol = Math.min(week.length, startCol + 1);
      }

      const startDay = week[startCol];
      const startDayStr = startDay ? toDateStr(startDay) : '';
      const roundedL = startDayStr === e.event_date;
      // Round right if: no end_date, end_date equals start (same day), or this segment ends the event
      const endDay = endCol < week.length ? week[endCol] : null;
      const endDayStr = endDay ? toDateStr(endDay) : '';
      const roundedR = !e.end_date || e.end_date === e.event_date || endDayStr === e.end_date;
      const showTitle = roundedL; // show title when this segment starts the event

      // Find the first available lane for this event
      let lane = 0;
      while (lane < lanes.length) {
        const occupied = lanes[lane].some(seg =>
          !(endCol <= seg.startCol || startCol >= seg.endCol)
        );
        if (!occupied) break;
        lane++;
      }

      // Create new lane if needed
      if (lane >= lanes.length) {
        lanes.push([]);
      }

      // Mark this segment as occupying space in this lane
      lanes[lane].push({ startCol, endCol });

      segments.push({ event: e, startCol, endCol, roundedL, roundedR, showTitle, lane });
    });

    return segments;
  };

  // Agenda view for mobile
  const renderAgendaView = () => {
    // Group all events by day, excluding weekends
    const eventsByDay = events.reduce((acc, event) => {
      const eventDate = parseISO(event.event_date);
      const dayOfWeek = eventDate.getDay(); // 0 = Sunday, 6 = Saturday
      
      // Skip weekends
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return acc;
      }
      
      const dateKey = format(eventDate, 'yyyy-MM-dd');

      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: eventDate,
          events: []
        };
      }
      acc[dateKey].events.push(event);
      return acc;
    }, {} as Record<string, { date: Date; events: CalendarEvent[] }>);

    const sortedDays = Object.values(eventsByDay)
      .map(day => ({
        ...day,
        // Sort events within each day: all-day first, then by time
        events: day.events.sort((a, b) => {
          if (a.all_day && !b.all_day) return -1;
          if (!a.all_day && b.all_day) return 1;
          if (!a.event_time && b.event_time) return -1;
          if (a.event_time && !b.event_time) return 1;
          if (a.event_time && b.event_time) {
            return a.event_time.localeCompare(b.event_time);
          }
          return 0;
        })
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return (
      <div className="space-y-4 overflow-y-auto flex-1 scrollbar-hide">
        {sortedDays.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No events available
          </div>
        ) : (
          sortedDays.map(({ date, events: dayEvents }) => (
            <div key={date.toString()} className="space-y-2">
              <div className={`font-semibold text-base ${isToday(date) ? 'text-primary' : 'text-foreground'}`}>
                {format(date, 'EEEE, MMMM d')}
                {isToday(date) && <span className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Today</span>}
              </div>
              <div className="space-y-2">
                {dayEvents.map(event => (
                  <Card
                    key={event.id}
                    className="p-3 border-l-4 hover:shadow-md transition-shadow cursor-pointer"
                    style={{ borderLeftColor: event.color || '#3b82f6' }}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{event.title}</h3>
                        {event.event_time && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {event.all_day ? 'All day' : formatTime12Hour(event.event_time)}
                            {event.end_time && !event.all_day && ` - ${formatTime12Hour(event.end_time)}`}
                          </p>
                        )}
                        {event.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{event.description}</p>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1 flex-shrink-0">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                              >
                                <Palette className="h-3 w-3" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-3">
                              <div className="space-y-2">
                                <p className="text-xs text-muted-foreground">Event Color</p>
                                <div className="grid grid-cols-6 gap-1.5">
                                  {CALENDAR_COLORS.map((color) => (
                                    <button
                                      key={color.value}
                                      onClick={() => handleColorChange(event.id, color.value)}
                                      className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 ${event.color === color.value ? 'border-foreground' : 'border-transparent'
                                        }`}
                                      style={{ backgroundColor: color.value }}
                                      title={color.name}
                                    />
                                  ))}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onEventEdit?.(event)}
                            className="h-7 w-7"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onEventDelete?.(event.id)}
                            className="h-7 w-7"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <Card className="p-4 bg-cha-logo-light backdrop-blur-sm border-primary/20 shadow-elegant h-full flex flex-col">
      <div className={`flex items-center mb-4 relative ${isMobile ? 'justify-center' : 'justify-between'}`}>
        <h2 className={`text-xl font-display font-bold text-foreground ${isMobile ? 'hidden' : 'flex-1'}`}>
          {format(currentDate, 'MMMM yyyy')}
        </h2>
        {calendarName && (
          <h1 className={`font-display font-bold text-foreground ${isMobile ? 'text-lg w-full text-center pt-1 pb-2' : 'text-2xl absolute left-1/2 transform -translate-x-1/2'}`}>
            {calendarName}
          </h1>
        )}
        {!isMobile && (
          <div className="flex gap-2 flex-1 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              className="border-primary/30 hover:bg-primary/10"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={previousMonth}
              disabled={!isAdmin && subMonths(currentDate, 1) < twoYearsAgo}
              className="border-primary/30 hover:bg-primary/10"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={nextMonth}
              disabled={!isAdmin && addMonths(currentDate, 1) > twoYearsFromNow}
              className="border-primary/30 hover:bg-primary/10"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {isMobile ? (
        renderAgendaView()
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="grid grid-cols-5 gap-0 mb-1">
            {weekDays.map(day => (
              <div
                key={day}
                className="text-center font-semibold text-muted-foreground py-2 text-sm bg-cha-logo-light"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="flex-1 flex flex-col">
            {weeks.map((week, weekIndex) => {
              // Check if this week contains first or last day of current month (filter out nulls)
              const validDays = week.filter((d): d is Date => d !== null);
              const hasFirstDayOfMonth = validDays.some(d => d.getMonth() === currentDate.getMonth() && d.getDate() === 1);
              const hasLastDayOfMonth = validDays.some(d => d.getMonth() === currentDate.getMonth() && d.getDate() === endOfMonth(currentDate).getDate());

              return (
                <div key={weekIndex} className="relative flex-1 min-h-0">
                  {/* Bars overlay spanning the full week */}
                  <div className="absolute inset-x-0 top-10 z-10">
                    <div className="grid grid-cols-5 gap-0 relative">
                      <TooltipProvider>
                        {getWeekSegments(week).map((seg, i) => {
                          // The overlay only shows all-day events now, so all segments should have background
                          const hasBackground = seg.event.all_day;
                          
                          return (
                            <Tooltip key={`${seg.event.id}-${i}`}>
                              <TooltipTrigger asChild>
                                <div
                                  className={`h-7 absolute flex items-center text-xs font-medium cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis ml-1 mr-1 ${hasBackground && seg.roundedL && !seg.roundedR ? 'rounded-l-md' :
                                      hasBackground && seg.roundedR && !seg.roundedL ? 'rounded-r-md' :
                                        hasBackground && seg.roundedL && seg.roundedR ? 'rounded-md' : ''
                                    }`}
                                  style={{
                                    left: `${(seg.startCol / 5) * 100}%`,
                                    width: `calc(${((seg.endCol - seg.startCol) / 5) * 100}% - 0.5rem)`,
                                    top: `${seg.lane * 30}px`,
                                    backgroundColor: hasBackground ? (seg.event.color || '#3b82f6') : 'transparent',
                                    color: hasBackground ? 'white' : 'black',
                                    paddingLeft: seg.roundedL ? '0.5rem' : '0rem',
                                    paddingRight: seg.roundedR ? '0.5rem' : '0rem',
                                  }}
                                >
                                  {seg.showTitle ? seg.event.title : ''}
                                </div>
                              </TooltipTrigger>
                            <TooltipContent className="max-w-sm">
                              <div className="space-y-2">
                                <p className="font-semibold">{seg.event.title}</p>
                                {seg.event.event_time && (
                                  <p className="text-sm text-muted-foreground">
                                    {seg.event.all_day ? 'All day' : formatTime12Hour(seg.event.event_time)}
                                    {seg.event.end_time && !seg.event.all_day && ` - ${formatTime12Hour(seg.event.end_time)}`}
                                  </p>
                                )}
                                {!isAdmin && seg.event.description && (
                                  <p className="text-sm">{seg.event.description}</p>
                                )}
                                {isAdmin && (
                                  <>
                                    <div className="pt-2 border-t">
                                      <p className="text-xs text-muted-foreground mb-2">Event Color</p>
                                      <div className="grid grid-cols-6 gap-1.5">
                                        {CALENDAR_COLORS.map((color) => (
                                          <button
                                            key={color.value}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleColorChange(seg.event.id, color.value);
                                            }}
                                            className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 ${seg.event.color === color.value ? 'border-foreground' : 'border-transparent'
                                              }`}
                                            style={{ backgroundColor: color.value }}
                                            title={color.name}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    {seg.event.description && (
                                      <div className="pt-2 border-t">
                                        <p className="text-sm">{seg.event.description}</p>
                                      </div>
                                    )}
                                    <div className="flex gap-2 pt-2 border-t">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEventEdit?.(seg.event);
                                        }}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Edit
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEventDelete?.(seg.event.id);
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Delete
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                          );
                        })}
                      </TooltipProvider>
                    </div>
                  </div>

                  {/* Day cells underlay */}
                  <div className="grid grid-cols-5 gap-0 h-full">
                    {week.map((day, dayIndex) => {
                      // Handle null padding (empty cells for alignment)
                      if (day === null) {
                        return (
                          <div key={`empty-${weekIndex}-${dayIndex}`} className="p-1.5 bg-cha-logo-light border border-black min-h-[60px]" />
                        );
                      }
                      
                      const isCurrentDay = isToday(day);
                      const isCurrentMonth = day.getMonth() === currentDate.getMonth();

                      // Check adjacent cells for border thickness
                      const leftDay = dayIndex > 0 ? week[dayIndex - 1] : null;
                      const rightDay = dayIndex < 4 ? week[dayIndex + 1] : null;
                      const leftIsOtherMonth = leftDay && leftDay !== null && leftDay.getMonth() !== currentDate.getMonth();
                      const rightIsOtherMonth = rightDay && rightDay !== null && rightDay.getMonth() !== currentDate.getMonth();

                      // Determine border thickness
                      const leftBorderThick = isCurrentMonth && dayIndex === 0;
                      const rightBorderThick = isCurrentMonth && dayIndex === 4;
                      const topBorderThick = isCurrentMonth && hasFirstDayOfMonth;
                      const bottomBorderThick = isCurrentMonth && hasLastDayOfMonth;

                      const borderClasses = isCurrentMonth
                        ? `${topBorderThick ? 'border-t-2' : 'border-t'} ${bottomBorderThick ? 'border-b-2' : 'border-b'} ${leftBorderThick ? 'border-l-2' : 'border-l'} ${rightBorderThick ? 'border-r-2' : 'border-r'} border-black`
                        : 'border border-black';

                      // Get all-day events for this day
                      const dayStr = toDateStr(day);
                      // Get events for this day and categorize them
                      const dayEvents = getEventsForDay(day);
                      const allDayEvents = dayEvents.filter(e => e.all_day);
                      // Get all timed events (non-all-day) and sort by time
                      const timedEvents = dayEvents
                        .filter(e => !e.all_day)
                        .sort((a, b) => {
                          // Sort by time if both have times
                          if (a.event_time && b.event_time) {
                            return a.event_time.localeCompare(b.event_time);
                          }
                          // Events with times come before events without
                          if (a.event_time) return -1;
                          if (b.event_time) return 1;
                          return 0;
                        });

                      // Color scheme based on order: 1st = orange, 2nd = red, 3rd = green
                      const getEventColorByIndex = (index: number): string => {
                        if (index === 0) return '#F97316'; // Orange - 1st timed entry
                        if (index === 1) return '#DC2626'; // Red - 2nd timed entry
                        if (index === 2) return '#059669'; // Green - 3rd timed entry
                        return '#000000'; // Default black for 4th+ entries
                      };

                      const renderEventLine = (events: CalendarEvent[], lineIndex: number, isAllDay: boolean = false, colorIndex?: number) => {
                        if (events.length === 0) return null;
                        
                        // For all-day events, render them horizontally in a flex container
                        if (isAllDay) {
                          return (
                            <TooltipProvider key={`all-day-${lineIndex}`}>
                              <div className="flex items-center justify-center gap-1 min-w-0 flex-1 flex-wrap">
                                {events.map((event) => (
                                  <Tooltip key={event.id}>
                                    <TooltipTrigger asChild>
                                      <div
                                        className="text-xs font-medium truncate cursor-pointer transition-opacity px-1.5 py-0.5 rounded text-white hover:opacity-80"
                                        style={{ backgroundColor: event.color || '#3b82f6' }}
                                        onClick={() => setSelectedEvent(event)}
                                      >
                                        {event.title}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm">
                                      <div className="space-y-2">
                                        <p className="font-semibold">{event.title}</p>
                                        {event.event_time && (
                                          <p className="text-sm text-muted-foreground">
                                            {event.all_day ? 'All day' : formatTime12Hour(event.event_time)}
                                          </p>
                                        )}
                                        {event.description && (
                                          <p className="text-sm">{event.description}</p>
                                        )}
                                        {isAdmin && (
                                          <div className="flex gap-2 pt-2 border-t">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onEventEdit?.(event);
                                              }}
                                            >
                                              <Edit className="h-3 w-3 mr-1" />
                                              Edit
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                onEventDelete?.(event.id);
                                              }}
                                            >
                                              <Trash2 className="h-3 w-3 mr-1" />
                                              Delete
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </TooltipProvider>
                          );
                        }
                        
                        // For timed events, render the event (should only be one per call now)
                        const event = events[0]; // We now pass one event at a time
                        if (!event) return null;
                        
                        // Use colorIndex if provided, otherwise default to black
                        const eventColor = colorIndex !== undefined ? getEventColorByIndex(colorIndex) : '#000000';
                        return (
                          <TooltipProvider key={`timed-${lineIndex}-${event.id}`}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className="text-xs font-medium cursor-pointer hover:underline w-full font-semibold break-words whitespace-normal"
                                  style={{ 
                                    color: eventColor,
                                    paddingLeft: '0.5rem',
                                    textIndent: '-0.5rem'
                                  }}
                                  onClick={() => setSelectedEvent(event)}
                                >
                                  {event.title}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                <div className="space-y-2">
                                  <p className="font-semibold">{event.title}</p>
                                  {event.event_time && (
                                    <p className="text-sm text-muted-foreground">
                                      {formatTime12Hour(event.event_time)}
                                    </p>
                                  )}
                                  {event.description && (
                                    <p className="text-sm">{event.description}</p>
                                  )}
                                  {isAdmin && (
                                    <div className="flex gap-2 pt-2 border-t">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEventEdit?.(event);
                                        }}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Edit
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEventDelete?.(event.id);
                                        }}
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Delete
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      };

                      return (
                        <div
                          key={day.toString()}
                          className={`p-1.5 relative bg-cha-logo-light z-20 ${borderClasses}`}
                        >
                          <div className="flex flex-col gap-0.5">
                            {/* First line: Date number and all-day events */}
                            <div className="flex items-center gap-1.5">
                              <span className={`text-sm font-medium flex-shrink-0 ${isCurrentDay
                                  ? 'bg-green-500 text-white font-bold w-7 h-7 flex items-center justify-center rounded-full'
                                  : isCurrentMonth
                                    ? 'text-foreground'
                                    : 'text-muted-foreground'
                                }`}>
                                {format(day, 'd')}
                              </span>
                              {renderEventLine(allDayEvents, 0, true)}
                            </div>
                            
                            {/* Show all timed events - each on its own line, colored by order */}
                            {timedEvents.length > 0 && (
                              <div className="flex flex-col gap-0.5">
                                {timedEvents.map((event, idx) => (
                                  <div key={`timed-${event.id}-${idx}`} className="pl-9">
                                    {renderEventLine([event], idx + 1, false, idx)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center rounded-lg">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      )}

      {/* Event Details Dialog for Mobile */}
      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">{selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: selectedEvent.color || '#3b82f6' }}
                />
                <div className="text-sm text-muted-foreground">
                  {format(parseISO(selectedEvent.event_date), 'EEEE, MMMM d, yyyy')}
                </div>
              </div>

              {selectedEvent.event_time && (
                <div className="text-sm">
                  <span className="font-medium">Time: </span>
                  {selectedEvent.all_day ? 'All day' : (
                    <>
                      {formatTime12Hour(selectedEvent.event_time)}
                      {selectedEvent.end_time && !selectedEvent.all_day && ` - ${formatTime12Hour(selectedEvent.end_time)}`}
                    </>
                  )}
                </div>
              )}

              {selectedEvent.description && (
                <div className="text-sm">
                  <span className="font-medium block mb-1">Description:</span>
                  <p className="text-muted-foreground whitespace-pre-wrap text-base">{selectedEvent.description}</p>
                </div>
              )}

              {isAdmin && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedEvent(null);
                      onEventEdit?.(selectedEvent);
                    }}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedEvent(null);
                      onEventDelete?.(selectedEvent.id);
                    }}
                    className="flex-1"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomCalendar;
